import { isFeatureEnabled } from "@picovico/utils";
import { FeatureKey } from "custom-types";

/**
 * Entry points (nav items, footer links, CTAs, quick links) that lead to a page
 * that exists only for one company feature. The tenant's nav and footer are
 * template JSON served by amphitheater, so they cannot know which features the
 * company has; the web app drops the dead entries at render time instead.
 */
interface FeatureLinkRule {
  feature: FeatureKey;
  /** True when `path` (no origin, query or hash) belongs to the feature's pages. */
  matchesPath: (path: string) => boolean;
  /** `page_type` values of the feature's pages, for entries that name a page. */
  pageTypes: readonly string[];
}

const FEATURE_LINK_RULES: readonly FeatureLinkRule[] = [
  {
    feature: "concession.web_food_ordering",
    matchesPath: (path) => path === "/food" || path.startsWith("/food/"),
    pageTypes: ["food_page"],
  },
];

/** Keys a config item keeps its destination under. */
const LINK_KEYS = ["href", "link", "url", "path", "to", "route"] as const;

const ORIGIN = /^https?:\/\/[^/]+/i;

function toPath(value: string): string {
  const withoutOrigin = value.trim().replace(ORIGIN, "");
  const [path] = withoutOrigin.split(/[?#]/);
  const trimmed = path.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

type PlainObject = Record<string, unknown>;

function isPlainObject(value: unknown): value is PlainObject {
  if (!value || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function pointsAtRule(item: PlainObject, rule: FeatureLinkRule): boolean {
  if (
    typeof item.page_type === "string" &&
    rule.pageTypes.includes(item.page_type)
  ) {
    return true;
  }
  return LINK_KEYS.some((key) => {
    const target = item[key];
    return typeof target === "string" && rule.matchesPath(toPath(target));
  });
}

function isEmptiedMenu(original: PlainObject, stripped: PlainObject): boolean {
  return (
    Array.isArray(original.subLinks) &&
    original.subLinks.length > 0 &&
    Array.isArray(stripped.subLinks) &&
    stripped.subLinks.length === 0 &&
    !stripped.href
  );
}

function strip(value: unknown, rules: readonly FeatureLinkRule[]): unknown {
  if (Array.isArray(value)) {
    const kept: unknown[] = [];
    for (const entry of value) {
      if (
        isPlainObject(entry) &&
        rules.some((rule) => pointsAtRule(entry, rule))
      ) {
        continue;
      }
      const stripped = strip(entry, rules);
      // A dropdown whose every sub-link was removed is an empty menu: drop it.
      if (
        isPlainObject(entry) &&
        isPlainObject(stripped) &&
        isEmptiedMenu(entry, stripped)
      ) {
        continue;
      }
      kept.push(stripped);
    }
    return kept;
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, strip(child, rules)]),
    );
  }
  return value;
}

/**
 * `value` (template props, a page's components, a list of nav items) without
 * the list entries that point at pages of a feature the company has off.
 *
 * Only entries of arrays are dropped (menu items, footer links, carousel
 * slides, quick links); a lone object prop such as `ctaButton: {href}` is left
 * alone because a component may read it unconditionally. Fail-open: with no
 * `features`, or every related feature on, the same `value` is returned.
 */
export function withoutDisabledFeatureLinks<T>(
  features: Partial<Record<string, boolean>> | null | undefined,
  value: T,
): T {
  const rules = FEATURE_LINK_RULES.filter(
    (rule) => !isFeatureEnabled(features, rule.feature),
  );
  if (rules.length === 0 || value == null) return value;
  return strip(value, rules) as T;
}
